import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { io } from "socket.io-client";
import { toast } from "react-toastify";
import { ArrowPathIcon, ClockIcon, DocumentDuplicateIcon, ExclamationTriangleIcon, PauseIcon } from "@heroicons/react/24/outline";
import { getCronInformation, setCronPaused, updateCronInformation } from "../../slices/cronSlice";
import { LOW_STOCK_SOLD_OUT_ALERTS_TYPE } from "../../consts/cronTypes";
import { LoaderSpinner } from "../../components/LoaderSpinner";
import { ChevronIcon, EditIcon, FilterIcon, KebabIcon, PlayIcon, SearchIcon, SortIcon } from "../reports/reportIcons";
import { CRON_CATEGORIES, MANUAL_RUN_TYPES } from "./cronCatalog";
import {
    CRON_STATUS_LABELS,
    deriveCronRow,
    filterCronRows,
    formatCronCompact,
    formatCronRelative,
    formatCronTime,
    matchesCronStatus,
    sortCronRows,
} from "./cronSchedule";
import CronDetailsDrawer from "./CronDetailsDrawer";
import CronLogo from "./CronLogo";
import CronStatusPill from "./CronStatusPill";

const getSocketServer = () => (
    window.location.origin.indexOf("localhost") !== -1 ? "http://localhost:3001" : window.location.origin
);

const socket = io(getSocketServer());

const STATUS_OPTIONS = [
    ["all", "All statuses"],
    ["attention", "Needs attention"],
    null,
    ["COMPLETED", CRON_STATUS_LABELS.COMPLETED],
    ["WARNING", CRON_STATUS_LABELS.WARNING],
    ["ERROR", CRON_STATUS_LABELS.ERROR],
    ["IN_PROGRESS", CRON_STATUS_LABELS.IN_PROGRESS],
    ["missed", "Missed last run"],
    ["NEVER", CRON_STATUS_LABELS.NEVER],
    ["UNKNOWN", CRON_STATUS_LABELS.UNKNOWN],
    ["PAUSED", CRON_STATUS_LABELS.PAUSED],
];
const KIND_OPTIONS = [["all", "All"], ["job", "Jobs"], ["report", "Reports"]];
const RUN_TIMEOUT_MS = 15 * 60 * 1000;

const toolbarButton = "inline-flex h-[34px] items-center gap-[7px] rounded-[7px] border border-[#ececef] bg-white px-[13px] text-[13px] font-medium text-[#0f1115] hover:bg-[#fafafb]";
const menuPanel = "absolute top-[calc(100%+4px)] z-30 min-w-[200px] rounded-lg border border-[#ececef] bg-white p-1 shadow-[0_8px_24px_rgba(15,17,21,0.08)]";
const menuItem = "flex h-[30px] w-full items-center gap-2 rounded-md px-2.5 text-left text-[12.5px] text-[#0f1115] hover:bg-[#f3f3f5] disabled:cursor-not-allowed disabled:opacity-50";
const iconButton = "inline-flex h-[26px] w-[26px] items-center justify-center rounded-md text-[#7b8090] hover:bg-[#f3f3f5] hover:text-[#0f1115] disabled:cursor-not-allowed disabled:opacity-40";
const gridEdit = "md:grid-cols-[22px_minmax(0,1fr)_112px_136px_136px_84px] xl:grid-cols-[22px_minmax(0,1fr)_112px_150px_150px_104px_84px]";
const gridView = "md:grid-cols-[minmax(0,1fr)_112px_136px_136px_32px] xl:grid-cols-[minmax(0,1fr)_112px_150px_150px_104px_32px]";

const Highlight = ({ text, query }) => {
    const value = String(text ?? "");
    const q = query.trim().toLowerCase();
    const index = q ? value.toLowerCase().indexOf(q) : -1;
    if (index < 0) return value;
    return (
        <>
            {value.slice(0, index)}
            <mark className="rounded-sm bg-[#fef3c7] text-inherit">{value.slice(index, index + q.length)}</mark>
            {value.slice(index + q.length)}
        </>
    );
};

const showUndoToast = (message, onUndo) => toast(({ closeToast }) => (
    <div className="flex items-center justify-between gap-3 text-[13px]">
        <span>{message}</span>
        <button type="button" onClick={() => { closeToast(); onUndo(); }} className="font-semibold text-[#0f1115] underline">
            Undo
        </button>
    </div>
), { position: "bottom-right" });

const SortHeader = ({ label, column, sort, onSort }) => {
    const active = sort.column === column;
    return (
        <button
            type="button"
            onClick={() => onSort(column)}
            className={`inline-flex items-center gap-1 text-left hover:text-[#0f1115] ${active ? "text-[#0f1115]" : ""}`}
        >
            <span>{label}</span>
            <SortIcon className={`h-[12px] w-[12px] shrink-0 ${active ? "" : "opacity-40"}`} />
        </button>
    );
};

const Dropdown = ({ open, onToggle, label, icon, children, align = "left", buttonClassName = "" }) => (
    <div className="relative" data-cron-menu>
        <button type="button" aria-haspopup="listbox" aria-expanded={open} onClick={onToggle} className={`${toolbarButton} ${buttonClassName}`}>
            {icon}
            {label}
            <ChevronIcon className="h-[14px] w-[14px] text-[#7b8090]" />
        </button>
        {open && <div role="listbox" className={`${menuPanel} ${align === "right" ? "right-0" : "left-0"}`}>{children}</div>}
    </div>
);

const LastRunCell = ({ row, now }) => {
    if (row.isRunning) {
        return (
            <div className="text-[12.5px] text-[#0f1115]">
                Running now
                <small className="block text-[11px] text-[#a2a6b3]">started {formatCronTime(row.runRequestedAt ?? now)}</small>
            </div>
        );
    }
    if (row.lastRunAt == null) {
        return (
            <div className="text-[12.5px] text-[#7b8090]">
                Never
                <small className="block text-[11px] text-[#a2a6b3]">No run recorded</small>
            </div>
        );
    }
    return (
        <div className="text-[12.5px] text-[#0f1115]">
            {formatCronRelative(row.lastRunAt, now)}
            {row.missedAt && <span className="font-medium text-[#c2410c]"> · late</span>}
            <small className="block text-[11px] tabular-nums text-[#a2a6b3]">{formatCronCompact(row.lastRunAt)}</small>
        </div>
    );
};

const NextRunCell = ({ row, now }) => {
    if (row.isPaused) {
        return <div className="text-[12.5px] text-[#7b8090]">Paused<small className="block text-[11px] text-[#a2a6b3]">Resume to schedule</small></div>;
    }
    if (!row.cronInterval) {
        return <div className="text-[12.5px] text-[#7b8090]">No schedule<small className="block text-[11px] text-[#a2a6b3]">Runs on demand</small></div>;
    }
    if (row.nextRunAt == null) return <div className="text-[12.5px] text-[#7b8090]">—</div>;
    return (
        <div className="text-[12.5px] text-[#0f1115]">
            {formatCronRelative(row.nextRunAt, now)}
            <small className="block text-[11px] tabular-nums text-[#a2a6b3]">{formatCronCompact(row.nextRunAt)}</small>
        </div>
    );
};

const MessageLine = ({ row, query }) => {
    if (row.message) {
        const tone = row.settledStatus === "ERROR" ? "text-[#b91c1c]" : row.isPaused ? "text-[#7b8090]" : "text-[#c2410c]";
        return (
            <div className={`mt-[3px] flex items-center gap-[5px] text-[12px] ${tone}`} title={row.message}>
                <ExclamationTriangleIcon className="h-3 w-3 flex-none" />
                <span className="truncate"><Highlight text={row.isPaused ? `Last run before pause: ${row.message}` : row.message} query={query} /></span>
            </div>
        );
    }
    if (row.missedAt) {
        return (
            <div className="mt-[3px] flex items-center gap-[5px] text-[12px] text-[#c2410c]">
                <ClockIcon className="h-3 w-3 flex-none" />
                <span className="truncate">Missed the {formatCronCompact(row.missedAt)} run</span>
            </div>
        );
    }
    return null;
};

const NameCell = ({ row, query }) => (
    <div className="flex min-w-0 items-start gap-2.5">
        <CronLogo type={row.type} />
        <div className="min-w-0">
            <div className="flex items-center gap-2">
                <span className="truncate text-[13px] font-semibold tracking-[-.005em] text-[#0f1115]"><Highlight text={row.name} query={query} /></span>
                {row.comingSoon && <span className="inline-flex h-[18px] flex-none items-center rounded bg-[#f3f3f5] px-1.5 text-[10.5px] font-medium text-[#7b8090]">Coming soon</span>}
                {row.kind === "report" && <span className="inline-flex h-[18px] flex-none items-center rounded bg-[#f3f3f5] px-1.5 text-[10.5px] font-medium text-[#7b8090]">Report</span>}
            </div>
            <div className="mt-px truncate text-[12px] text-[#7b8090]">
                {row.schedule
                    ? <Highlight text={row.schedule} query={query} />
                    : row.cronInterval ? <span className="font-mono"><Highlight text={row.cronInterval} query={query} /></span> : "No schedule"}
                {" · "}
                <span className="font-mono text-[11.5px]"><Highlight text={row.type} query={query} /></span>
            </div>
            <MessageLine row={row} query={query} />
        </div>
    </div>
);

const Cron = ({ access }) => {
    const dispatch = useDispatch();
    const { cronInformation, loading, error } = useSelector((state) => state.cron);
    const canEdit = access === "edit";

    const [query, setQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [categoryFilter, setCategoryFilter] = useState(null);
    const [kindFilter, setKindFilter] = useState("all");
    const [sort, setSort] = useState({ column: null, direction: "asc" });
    const [selected, setSelected] = useState(() => new Set());
    const [openKey, setOpenKey] = useState(null);
    const [focusSchedule, setFocusSchedule] = useState(false);
    const [menu, setMenu] = useState(null);
    const [runRequests, setRunRequests] = useState({});
    const [savingKey, setSavingKey] = useState(null);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [now, setNow] = useState(() => Date.now());
    const searchRef = useRef(null);

    const refresh = useCallback((silent = true) => dispatch(getCronInformation({ silent })), [dispatch]);

    useEffect(() => {
        refresh(false).finally(() => setHasLoaded(true));
    }, [refresh]);

    const rows = useMemo(() => {
        const defaults = (cronInformation?.defaults || []).map((cron) => ({ cron, kind: "job" }));
        const reports = (cronInformation?.reports || [])
            .filter((cron) => cron.type !== LOW_STOCK_SOLD_OUT_ALERTS_TYPE)
            .map((cron) => ({ cron, kind: "report" }));
        return [...defaults, ...reports].map(({ cron, kind }) => deriveCronRow(cron, {
            kind,
            now,
            runRequest: runRequests[`${kind}:${cron.type}`] || null,
        }));
    }, [cronInformation, now, runRequests]);

    const anyRunning = rows.some((row) => row.isRunning);

    useEffect(() => {
        const tick = setInterval(() => setNow(Date.now()), anyRunning ? 1000 : 30000);
        return () => clearInterval(tick);
    }, [anyRunning]);

    useEffect(() => {
        const poll = setInterval(() => refresh(true), anyRunning ? 5000 : 60000);
        return () => clearInterval(poll);
    }, [anyRunning, refresh]);

    useEffect(() => {
        const keys = Object.keys(runRequests);
        if (!keys.length) return;
        const finished = [];
        rows.forEach((row) => {
            const request = runRequests[row.key];
            if (!request) return;
            const serverStatus = row.cron?.displayStatus ?? row.cron?.processStatus;
            const lastRun = row.lastRunAt;
            if (serverStatus !== "IN_PROGRESS" && lastRun != null && lastRun >= request.at - 5000) {
                finished.push(row.key);
                const label = CRON_STATUS_LABELS[row.settledStatus] || "Finished";
                const notify = row.settledStatus === "ERROR" ? toast.error : row.settledStatus === "WARNING" ? toast.warn : toast.success;
                notify(`${row.name} finished: ${label}.`, { position: "bottom-right" });
            } else if (Date.now() - request.at > RUN_TIMEOUT_MS) {
                finished.push(row.key);
            }
        });
        if (finished.length) {
            setRunRequests((current) => {
                const next = { ...current };
                finished.forEach((key) => delete next[key]);
                return next;
            });
        }
    }, [rows, runRequests]);

    const categoryCounts = useMemo(() => rows.reduce((acc, row) => {
        acc[row.category] = (acc[row.category] || 0) + 1;
        return acc;
    }, {}), [rows]);

    const visibleRows = useMemo(() => sortCronRows(
        filterCronRows(rows, { query, status: statusFilter, category: categoryFilter, kind: kindFilter }),
        sort,
    ), [rows, query, statusFilter, categoryFilter, kindFilter, sort]);

    const openRow = rows.find((row) => row.key === openKey) || null;
    const stats = useMemo(() => ({
        attention: rows.filter((row) => row.attention).length,
        ERROR: rows.filter((row) => row.status === "ERROR").length,
        WARNING: rows.filter((row) => row.status === "WARNING").length,
        missed: rows.filter((row) => row.missedAt).length,
        NEVER: rows.filter((row) => row.status === "NEVER").length,
        IN_PROGRESS: rows.filter((row) => row.isRunning).length,
        PAUSED: rows.filter((row) => row.isPaused).length,
        nextHour: rows.filter((row) => row.nextRunAt != null && row.nextRunAt - now <= 3600e3).length,
    }), [rows, now]);

    const closeDrawer = () => { setOpenKey(null); setFocusSchedule(false); };
    const openDrawer = (row, { schedule = false } = {}) => {
        setOpenKey(row.key);
        setFocusSchedule(schedule);
        setMenu(null);
    };

    useEffect(() => {
        const onSlashKeyDown = (event) => {
            const target = event.target;
            const isEditableTarget = target instanceof HTMLElement && (
                target.tagName === "INPUT" ||
                target.tagName === "TEXTAREA" ||
                target.tagName === "SELECT" ||
                target.isContentEditable
            );
            if (event.key === "/" && !isEditableTarget) {
                event.preventDefault();
                searchRef.current?.focus();
            }
        };
        const onKeyDown = (event) => {
            if (event.key !== "Escape") return;
            if (menu) setMenu(null);
            else if (openKey) closeDrawer();
            else if (document.activeElement === searchRef.current) setQuery("");
        };
        const onPointerDown = (event) => {
            if (menu && !event.target.closest?.("[data-cron-menu]")) setMenu(null);
        };
        document.addEventListener("keydown", onSlashKeyDown, true);
        document.addEventListener("keydown", onKeyDown);
        document.addEventListener("mousedown", onPointerDown);
        return () => {
            document.removeEventListener("keydown", onSlashKeyDown, true);
            document.removeEventListener("keydown", onKeyDown);
            document.removeEventListener("mousedown", onPointerDown);
        };
    }, [menu, openKey]);

    const resetFilters = () => {
        setQuery("");
        setStatusFilter("all");
        setCategoryFilter(null);
        setKindFilter("all");
        setSort({ column: null, direction: "asc" });
    };

    const handleSort = (column) => setSort((current) => (current.column === column
        ? (current.direction === "asc" ? { column, direction: "desc" } : { column: null, direction: "asc" })
        : { column, direction: column === "last" ? "desc" : "asc" }));

    const runRow = (row, { quiet = false } = {}) => {
        const jobType = MANUAL_RUN_TYPES[row.type];
        if (!jobType) {
            if (!quiet) toast.error(`${row.name} has no manual run.`, { position: "bottom-right" });
            return false;
        }
        if (row.isPaused) {
            if (!quiet) toast.info(`${row.name} is paused. Resume it to run.`, { position: "bottom-right" });
            return false;
        }
        if (row.isRunning) return false;
        socket.emit("runCronJob", { type: jobType });
        setRunRequests((current) => ({ ...current, [row.key]: { at: Date.now(), attention: row.attention, rank: row.rank } }));
        if (!quiet) toast.success(`${row.name} started.`, { position: "bottom-right" });
        return true;
    };

    const setPaused = async (rowsToChange, isPaused) => {
        const targets = rowsToChange.filter((row) => row.cron?.id && row.isPaused !== isPaused);
        if (!targets.length) return [];
        const results = await Promise.allSettled(targets.map((row) => dispatch(setCronPaused({ cron: row.cron, isPaused })).unwrap()));
        const failed = targets.filter((_, index) => results[index].status === "rejected");
        if (failed.length) {
            toast.error(`Failed to ${isPaused ? "pause" : "resume"} ${failed.map((row) => row.name).join(", ")}.`, { position: "bottom-right" });
        }
        return targets.filter((_, index) => results[index].status === "fulfilled");
    };

    const togglePaused = async (row) => {
        const isPaused = !row.isPaused;
        const changed = await setPaused([row], isPaused);
        if (changed.length) {
            showUndoToast(`${row.name} ${isPaused ? "paused" : "resumed"}.`, () => setPaused(changed.map((r) => ({ ...r, isPaused })), !isPaused));
        }
    };

    const saveSchedule = async (row, cronInterval) => {
        const previous = row.cronInterval;
        setSavingKey(row.key);
        try {
            await dispatch(updateCronInformation({ singleData: { ...row.cron, cronInterval } })).unwrap();
            showUndoToast(`${row.name} schedule saved.`, async () => {
                try {
                    await dispatch(updateCronInformation({ singleData: { ...row.cron, cronInterval: previous } })).unwrap();
                } catch {
                    toast.error(`Failed to restore ${row.name} schedule.`, { position: "bottom-right" });
                }
            });
        } catch {
            toast.error(`Failed to save ${row.name} schedule.`, { position: "bottom-right" });
        } finally {
            setSavingKey(null);
        }
    };

    const copyType = async (row) => {
        try {
            await navigator.clipboard.writeText(row.type);
            toast.success(`Copied ${row.type}.`, { position: "bottom-right" });
        } catch {
            toast.error("Couldn't copy to the clipboard.", { position: "bottom-right" });
        }
    };

    const selectedRows = rows.filter((row) => selected.has(row.key));
    const toggleSelected = (key) => setSelected((current) => {
        const next = new Set(current);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });
    const allVisibleSelected = visibleRows.length > 0 && visibleRows.every((row) => selected.has(row.key));
    const toggleSelectAll = () => setSelected((current) => {
        const next = new Set(current);
        visibleRows.forEach((row) => (allVisibleSelected ? next.delete(row.key) : next.add(row.key)));
        return next;
    });

    const bulkRun = () => {
        const started = selectedRows.filter((row) => runRow(row, { quiet: true }));
        const skipped = selectedRows.length - started.length;
        toast.success(`Started ${started.length} job${started.length === 1 ? "" : "s"}${skipped ? `. Skipped ${skipped} (paused, running, or no manual run)` : ""}.`, { position: "bottom-right" });
        setSelected(new Set());
    };
    const bulkPause = async (isPaused) => {
        const changed = await setPaused(selectedRows, isPaused);
        setSelected(new Set());
        if (changed.length) {
            showUndoToast(`${isPaused ? "Paused" : "Resumed"} ${changed.length} job${changed.length === 1 ? "" : "s"}.`, () => setPaused(changed.map((r) => ({ ...r, isPaused })), !isPaused));
        }
    };

    const showGroups = !sort.column && statusFilter === "all" && visibleRows.some((row) => row.attention) && visibleRows.some((row) => !row.attention);
    const attentionCount = visibleRows.filter((row) => row.attention).length;
    const statusLabel = STATUS_OPTIONS.find((option) => option?.[0] === statusFilter)?.[1];
    const grid = canEdit ? gridEdit : gridView;
    const initialLoading = !hasLoaded || (loading && !rows.length);

    const quickStats = [
        ["attention", "Needs attention", stats.attention, "bg-[#f97316]"],
        ["ERROR", "Errors", stats.ERROR, "bg-[#ef4444]"],
        ["WARNING", "Warnings", stats.WARNING, "bg-[#fb923c]"],
        ["missed", "Missed last run", stats.missed, "bg-[#fdba74]"],
        ["NEVER", "Never run", stats.NEVER, "ring-[1.5px] ring-inset ring-[#9ca3af]"],
        ["IN_PROGRESS", "Running", stats.IN_PROGRESS, "bg-[#3b82f6]"],
        ["PAUSED", "Paused", stats.PAUSED, "bg-[#9ca3af]"],
    ];

    const renderStatusDropdown = (align, buttonClassName) => (
        <Dropdown
            open={menu === "status"}
            onToggle={() => setMenu(menu === "status" ? null : "status")}
            label={statusLabel}
            align={align}
            buttonClassName={buttonClassName}
            icon={<FilterIcon className="h-[14px] w-[14px] text-[#7b8090]" />}
        >
            {STATUS_OPTIONS.map((option, index) => (option ? (
                <button
                    key={option[0]}
                    type="button"
                    role="option"
                    aria-selected={statusFilter === option[0]}
                    onClick={() => { setStatusFilter(option[0]); setMenu(null); }}
                    className={`flex h-[30px] w-full items-center justify-between gap-3 rounded-md px-2.5 text-[12.5px] hover:bg-[#f3f3f5] ${statusFilter === option[0] ? "bg-[#f3f3f5] font-medium" : ""}`}
                >
                    <span>{option[1]}</span>
                    <span className="text-[11px] text-[#a2a6b3]">{rows.filter((row) => matchesCronStatus(row, option[0])).length}</span>
                </button>
            ) : <div key={`sep-${index}`} className="my-1 h-px bg-[#f3f3f5]" />))}
        </Dropdown>
    );

    const renderKindToggle = (heightClassName) => (
        <div className={`inline-flex items-center rounded-[7px] border border-[#ececef] bg-white p-0.5 normal-case tracking-normal ${heightClassName}`} role="group" aria-label="Job kind">
            {KIND_OPTIONS.map(([value, label]) => (
                <button
                    key={value}
                    type="button"
                    aria-pressed={kindFilter === value}
                    onClick={() => setKindFilter(value)}
                    className={`h-full rounded-[5px] px-[11px] text-[13px] font-medium ${kindFilter === value ? "bg-[#e6e6ea] text-[#0f1115]" : "text-[#7b8090] hover:text-[#0f1115]"}`}
                >
                    {label}
                </button>
            ))}
        </div>
    );

    const renderRowMenu = (row) => {
        const menuOpen = menu === `row:${row.key}`;
        return (
            <div className="relative" data-cron-menu>
                <button
                    type="button"
                    aria-label={`More actions for ${row.name}`}
                    aria-expanded={menuOpen}
                    onClick={(event) => { event.stopPropagation(); setMenu(menuOpen ? null : `row:${row.key}`); }}
                    className={`${iconButton} ${menuOpen ? "bg-[#f3f3f5] text-[#0f1115]" : ""}`}
                >
                    <KebabIcon className="h-[13px] w-[13px]" />
                </button>
                {menuOpen && (
                    <div className={`${menuPanel} right-0 min-w-[170px]`}>
                        <button type="button" className={menuItem} onClick={() => openDrawer(row)}>View details</button>
                        {canEdit && row.cron?.id && (
                            <button type="button" className={menuItem} onClick={() => openDrawer(row, { schedule: true })}>
                                <EditIcon className="h-[13px] w-[13px] text-[#7b8090]" />Edit schedule
                            </button>
                        )}
                        {canEdit && row.cron?.id && (
                            <button type="button" className={menuItem} onClick={() => { setMenu(null); togglePaused(row); }}>
                                {row.isPaused ? <PlayIcon className="h-[12px] w-[12px] text-[#7b8090]" /> : <PauseIcon className="h-[13px] w-[13px] text-[#7b8090]" />}
                                {row.isPaused ? "Resume" : "Pause"}
                            </button>
                        )}
                        <button type="button" className={menuItem} onClick={() => { setMenu(null); copyType(row); }}>
                            <DocumentDuplicateIcon className="h-[13px] w-[13px] text-[#7b8090]" />Copy job type
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const renderActions = (row) => (
        <div className="flex items-center justify-end gap-0.5" onClick={(event) => event.stopPropagation()}>
            {canEdit && (
                <button
                    type="button"
                    aria-label={`Run ${row.name} now`}
                    title={!MANUAL_RUN_TYPES[row.type] ? "No manual run" : row.isPaused ? "Resume to run" : "Run now"}
                    disabled={!MANUAL_RUN_TYPES[row.type] || row.isPaused || row.isRunning}
                    onClick={() => runRow(row)}
                    className={iconButton}
                >
                    {row.isRunning ? <ArrowPathIcon className="h-[14px] w-[14px] animate-spin" /> : <PlayIcon className="h-[12px] w-[12px]" />}
                </button>
            )}
            {canEdit && (
                <button
                    type="button"
                    aria-label={`${row.isPaused ? "Resume" : "Pause"} ${row.name}`}
                    title={row.isPaused ? "Resume" : "Pause"}
                    disabled={!row.cron?.id}
                    onClick={() => togglePaused(row)}
                    className={`${iconButton} hidden md:inline-flex`}
                >
                    {row.isPaused ? <PlayIcon className="h-[12px] w-[12px] text-[#10b981]" /> : <PauseIcon className="h-[14px] w-[14px]" />}
                </button>
            )}
            {renderRowMenu(row)}
        </div>
    );

    const renderRow = (row) => {
        const isSelected = selected.has(row.key);
        const isOpen = openKey === row.key;
        const onKeyDown = (event) => {
            if (event.target !== event.currentTarget) return;
            if (event.key === "Enter" || event.key === " ") { event.preventDefault(); openDrawer(row); }
        };
        const checkbox = canEdit && (
            <input
                type="checkbox"
                aria-label={`Select ${row.name}`}
                checked={isSelected}
                onChange={() => toggleSelected(row.key)}
                onClick={(event) => event.stopPropagation()}
                className="mt-[7px] h-[15px] w-[15px] cursor-pointer self-start accent-[#0f1115] md:mt-0 md:self-center"
            />
        );
        const rowTone = isSelected || isOpen ? "bg-[#f3f3f5]" : "hover:bg-[#fafafb]";
        return (
            <React.Fragment key={row.key}>
                <div
                    role="button"
                    tabIndex={0}
                    aria-label={`${row.name}, ${row.statusLabel}`}
                    onClick={() => openDrawer(row)}
                    onKeyDown={onKeyDown}
                    className={`group hidden min-h-[56px] cursor-pointer items-center gap-3 border-b border-[#f3f3f5] px-3.5 py-2 last:border-b-0 md:grid ${grid} ${rowTone}`}
                >
                    {checkbox}
                    <NameCell row={row} query={query} />
                    <div>
                        <CronStatusPill status={row.status} suffix={row.isRunning && row.runRequestedAt ? ` · ${Math.max(1, Math.round((now - row.runRequestedAt) / 1000))}s` : null} />
                    </div>
                    <LastRunCell row={row} now={now} />
                    <NextRunCell row={row} now={now} />
                    <div className="hidden xl:block">
                        <span className="inline-flex h-[22px] items-center rounded-md bg-[#f3f3f5] px-2 text-[12px] font-medium text-[#4b5060]">{row.category}</span>
                    </div>
                    {renderActions(row)}
                </div>

                <div
                    role="button"
                    tabIndex={0}
                    aria-label={`${row.name}, ${row.statusLabel}`}
                    onClick={() => openDrawer(row)}
                    onKeyDown={onKeyDown}
                    className={`flex cursor-pointer gap-2.5 border-b border-[#f3f3f5] px-3.5 py-3 last:border-b-0 md:hidden ${rowTone}`}
                >
                    {checkbox}
                    <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                            <NameCell row={row} query={query} />
                            {renderActions(row)}
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 pl-[38px] text-[12px] text-[#7b8090]">
                            <CronStatusPill status={row.status} />
                            <span>{row.lastRunAt != null ? `Last ${formatCronRelative(row.lastRunAt, now)}` : "Never run"}</span>
                            {row.nextRunAt != null && <span>· Next {formatCronRelative(row.nextRunAt, now)}</span>}
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    };

    const groupHeader = (label, count) => (
        <div className="flex h-8 items-center gap-2 border-b border-[#f3f3f5] bg-[#fcfcfd] px-3.5 text-[11px] font-semibold uppercase tracking-[.1em] text-[#7b8090]">
            {label}<span className="font-medium normal-case tracking-normal text-[#a2a6b3]">{count}</span>
        </div>
    );

    return (
        <div className="min-h-screen bg-[#f7f7f8] text-[#0f1115] antialiased">
            <div className="flex min-h-screen">
                <div className="flex min-h-screen min-w-0 flex-1 flex-col">
                    <header className="px-4 pt-3 md:px-7 md:pt-[14px]">
                        <div className="hidden text-[10.5px] font-semibold uppercase tracking-[.14em] text-[#7b8090] md:block">Eadin Internal Tool Suite</div>
                        <div className="flex items-center justify-between gap-2 md:mt-1.5 md:items-end md:gap-4">
                            <h1 className="truncate text-[18px] font-[650] tracking-[-.02em] md:text-[20px]">Cron Jobs</h1>
                            <div className="flex flex-none items-center gap-2 text-[12.5px] text-[#7b8090] md:gap-3">
                                <span aria-live="polite" className="hidden md:inline">
                                    {stats.IN_PROGRESS > 0 && <span className="font-medium text-[#1d4ed8]">{stats.IN_PROGRESS} running · </span>}
                                    {stats.nextHour} run{stats.nextHour === 1 ? "" : "s"} in the next hour
                                </span>
                                <div className="md:hidden">{renderStatusDropdown("right", "h-8 px-2.5 text-[12.5px]")}</div>
                                <button
                                    type="button"
                                    onClick={() => refresh(true)}
                                    aria-label="Refresh"
                                    className="inline-flex h-8 w-8 items-center justify-center gap-[7px] rounded-[7px] border border-[#ececef] bg-white text-[12.5px] font-medium text-[#0f1115] hover:bg-[#fafafb] md:h-[30px] md:w-auto md:px-2.5"
                                >
                                    <ArrowPathIcon className={`h-[15px] w-[15px] flex-none text-[#4b5060] ${loading ? "animate-spin" : ""}`} />
                                    <span className="hidden md:inline">Refresh</span>
                                </button>
                            </div>
                        </div>
                        <div className="mt-2.5 flex items-center gap-2.5 md:mt-3 md:flex-wrap">
                            <label className="flex h-9 min-w-0 flex-1 items-center gap-2.5 rounded-[10px] border border-[#ececef] bg-white px-3.5 shadow-[0_1px_2px_rgba(15,17,21,0.04)] focus-within:border-[#c9cbd3] md:min-w-[200px]">
                                <SearchIcon className="h-[15px] w-[15px] flex-none text-[#7b8090]" />
                                <input
                                    ref={searchRef}
                                    type="search"
                                    value={query}
                                    onChange={(event) => setQuery(event.target.value)}
                                    placeholder="Search jobs, types, schedules, or messages…"
                                    aria-label="Search cron jobs"
                                    className="min-w-0 flex-1 border-0 bg-transparent text-[13.5px] text-[#0f1115] outline-none placeholder:text-[#a2a6b3]"
                                />
                                <kbd className="hidden rounded border border-[#ececef] bg-[#f7f7f8] px-1.5 py-px font-sans text-[10.5px] text-[#a2a6b3] md:block">/</kbd>
                            </label>
                            <div className="hidden md:block">{renderStatusDropdown("left")}</div>
                            <div className="hidden md:block">
                                <Dropdown
                                    open={menu === "category"}
                                    onToggle={() => setMenu(menu === "category" ? null : "category")}
                                    label={categoryFilter || "All categories"}
                                >
                                    {[null, ...CRON_CATEGORIES].map((category) => (
                                        <button
                                            key={category || "all"}
                                            type="button"
                                            role="option"
                                            aria-selected={categoryFilter === category}
                                            onClick={() => { setCategoryFilter(category); setMenu(null); }}
                                            className={`flex h-[30px] w-full items-center justify-between gap-3 rounded-md px-2.5 text-[12.5px] hover:bg-[#f3f3f5] ${categoryFilter === category ? "bg-[#f3f3f5] font-medium" : ""}`}
                                        >
                                            <span>{category || "All categories"}</span>
                                            <span className="text-[11px] text-[#a2a6b3]">{category ? categoryCounts[category] || 0 : rows.length}</span>
                                        </button>
                                    ))}
                                </Dropdown>
                            </div>
                            <div className="hidden md:block">{renderKindToggle("h-[34px]")}</div>
                        </div>
                        {!initialLoading && rows.length > 0 && (
                            <div className="mt-3 hidden flex-wrap gap-1.5 md:flex">
                                {quickStats.filter(([, , count]) => count > 0).map(([value, label, count, dot]) => (
                                    <button
                                        key={value}
                                        type="button"
                                        aria-pressed={statusFilter === value}
                                        onClick={() => setStatusFilter(statusFilter === value ? "all" : value)}
                                        className={`inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[12px] font-medium ${
                                            statusFilter === value ? "border-[#0f1115] bg-[#0f1115] text-white" : "border-[#ececef] bg-white text-[#4b5060] hover:text-[#0f1115]"
                                        }`}
                                    >
                                        <i aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${dot}`} />
                                        {label}
                                        <span className={statusFilter === value ? "text-white/70" : "text-[#a2a6b3]"}>{count}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </header>

                    <div className="px-4 pb-24 pt-2.5 md:px-7 md:pt-4">
                        <div className="mb-2 flex items-center justify-between text-[11px] font-semibold uppercase tracking-[.1em] text-[#7b8090]">
                            <span className="hidden md:inline">{kindFilter === "report" ? "Report jobs" : kindFilter === "job" ? "Jobs" : "All jobs"}</span>
                            <div className="md:hidden">{renderKindToggle("h-[30px]")}</div>
                            <span className="font-medium normal-case tracking-normal text-[#a2a6b3]">{visibleRows.length} of {rows.length}</span>
                        </div>

                        <div className="rounded-[10px] border border-[#ececef] bg-white">
                            {initialLoading ? (
                                <div className="flex min-h-[240px] items-center justify-center"><LoaderSpinner /></div>
                            ) : error && !rows.length ? (
                                <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
                                    <p className="text-[13px] font-semibold">Couldn't load cron jobs</p>
                                    <p className="text-[12.5px] text-[#7b8090]">{typeof error === "string" && error ? error : "The request failed."}</p>
                                    <button type="button" onClick={() => refresh(false)} className="mt-2 inline-flex h-[30px] items-center rounded-[7px] border border-[#ececef] bg-white px-3 text-[12.5px] font-medium hover:bg-[#fafafb]">Try again</button>
                                </div>
                            ) : visibleRows.length === 0 ? (
                                <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
                                    <p className="text-[13px] font-semibold">No jobs match</p>
                                    <p className="text-[12.5px] text-[#7b8090]">Try a different search or clear the filters.</p>
                                    <button type="button" onClick={resetFilters} className="mt-2 inline-flex h-[30px] items-center rounded-[7px] border border-[#ececef] bg-white px-3 text-[12.5px] font-medium hover:bg-[#fafafb]">Reset filters</button>
                                </div>
                            ) : (
                                <>
                                    <div className={`hidden h-9 items-center gap-3 rounded-t-[10px] border-b border-[#ececef] bg-[#fafafb] px-3.5 text-[11.5px] font-semibold text-[#7b8090] md:grid ${grid}`}>
                                        {canEdit && (
                                            <input
                                                type="checkbox"
                                                aria-label="Select all visible jobs"
                                                checked={allVisibleSelected}
                                                onChange={toggleSelectAll}
                                                className="h-[15px] w-[15px] cursor-pointer accent-[#0f1115]"
                                            />
                                        )}
                                        <SortHeader label="Job" column="name" sort={sort} onSort={handleSort} />
                                        <SortHeader label="Status" column="status" sort={sort} onSort={handleSort} />
                                        <SortHeader label="Last run" column="last" sort={sort} onSort={handleSort} />
                                        <SortHeader label="Next run" column="next" sort={sort} onSort={handleSort} />
                                        <span className="hidden xl:block">Category</span>
                                        <span />
                                    </div>
                                    {showGroups ? (
                                        <>
                                            {groupHeader("Needs attention", attentionCount)}
                                            {visibleRows.filter((row) => row.attention).map(renderRow)}
                                            {groupHeader("Everything else", visibleRows.length - attentionCount)}
                                            {visibleRows.filter((row) => !row.attention).map(renderRow)}
                                        </>
                                    ) : visibleRows.map(renderRow)}
                                </>
                            )}
                        </div>
                        {!initialLoading && rows.length > 0 && (
                            <p className="mt-2.5 text-[11.5px] text-[#a2a6b3]">
                                Times shown in Mountain Time. "Missed" compares each job's latest run with its schedule; Eadin doesn't keep a full run log.
                            </p>
                        )}
                    </div>
                </div>

                {openRow && (
                    <CronDetailsDrawer
                        row={openRow}
                        now={now}
                        canEdit={canEdit}
                        canRun={!!MANUAL_RUN_TYPES[openRow.type]}
                        focusSchedule={focusSchedule}
                        isSaving={savingKey === openRow.key}
                        onClose={closeDrawer}
                        onRun={(row) => runRow(row)}
                        onTogglePaused={togglePaused}
                        onSaveSchedule={saveSchedule}
                        onCopyType={copyType}
                    />
                )}
            </div>

            {canEdit && selectedRows.length > 0 && (
                <div role="region" aria-label="Bulk actions" className="fixed bottom-5 left-1/2 z-[120] flex -translate-x-1/2 items-center gap-1 rounded-[10px] bg-[#0f1115] px-2 py-1.5 text-[12.5px] text-white shadow-[0_8px_24px_rgba(15,17,21,0.24)]">
                    <span className="px-2 font-medium">{selectedRows.length} selected</span>
                    <button type="button" onClick={bulkRun} className="h-7 rounded-md px-2.5 hover:bg-white/10">Run</button>
                    <button type="button" onClick={() => bulkPause(true)} className="h-7 rounded-md px-2.5 hover:bg-white/10">Pause</button>
                    <button type="button" onClick={() => bulkPause(false)} className="h-7 rounded-md px-2.5 hover:bg-white/10">Resume</button>
                    <button type="button" onClick={() => setSelected(new Set())} className="h-7 rounded-md px-2.5 text-white/70 hover:bg-white/10 hover:text-white">Clear</button>
                </div>
            )}
        </div>
    );
};

export default Cron;
